import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { type ReactNode, useState } from "react";
import RichTooltip from "~/common/components/RichTooltip/RichTooltip";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  styled,
} from "~/common/mui";
import type { ControlInfoViewModel } from "./controlViewModel";

export interface ControlInfoProps extends ControlInfoViewModel {
  className?: string;
}

const InfoLink = styled("button")(({ theme }) => ({
  appearance: "none",
  border: 0,
  padding: 0,
  background: "transparent",
  color: theme.palette.text.secondary,
  cursor: "pointer",
  ...theme.typography.bodySmall,
  fontStyle: "italic",
  textDecoration: "underline",
  textUnderlineOffset: theme.spacing(0.25),
  "&:hover, &:focus-visible": {
    color: theme.palette.text.primary,
  },
}));

const SmallInfoButton = styled(IconButton)(({ theme }) => ({
  width: theme.spacing(3),
  height: theme.spacing(3),
  padding: theme.spacing(0.25),
  color: theme.palette.text.secondary,
}));

const InfoIcon = () => <InfoOutlinedIcon sx={(theme) => ({ fontSize: theme.spacing(2.5) })} />;

export const ControlInfo = ({
  content,
  ariaLabel,
  trigger = "icon",
  presentation = "tooltip",
  linkLabel = "What is this?",
  dialogTitle,
  closeLabel = "Close",
  className,
}: ControlInfoProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const triggerElement =
    trigger === "link" ? (
      <InfoLink type="button" className={className} onClick={() => setDialogOpen(true)}>
        {linkLabel}
      </InfoLink>
    ) : (
      <SmallInfoButton className={className} aria-label={ariaLabel} onClick={() => setDialogOpen(true)}>
        <InfoIcon />
      </SmallInfoButton>
    );

  if (presentation === "richTooltip") {
    const richTooltipTrigger =
      trigger === "link" ? (
        <InfoLink type="button" className={className} aria-label={ariaLabel}>
          {linkLabel}
        </InfoLink>
      ) : (
        <SmallInfoButton className={className} aria-label={ariaLabel}>
          <InfoIcon />
        </SmallInfoButton>
      );

    return (
      <RichTooltip
        variant="rich"
        content={
          <Stack gap={1}>
            <Typography variant="titleSmall">{ariaLabel}</Typography>
            <Box
              sx={{
                typography: "bodyMedium",
                "& img": { display: "block", maxWidth: "100%", height: "auto" },
              }}
            >
              {content}
            </Box>
          </Stack>
        }
      >
        {richTooltipTrigger}
      </RichTooltip>
    );
  }

  if (presentation === "tooltip") {
    const tooltipTrigger =
      trigger === "link" ? (
        <InfoLink type="button" className={className} aria-label={ariaLabel}>
          {linkLabel}
        </InfoLink>
      ) : (
        <SmallInfoButton className={className} aria-label={ariaLabel}>
          <InfoIcon />
        </SmallInfoButton>
      );

    return (
      <Tooltip title={content} arrow>
        {tooltipTrigger}
      </Tooltip>
    );
  }

  return (
    <>
      {triggerElement}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} aria-label={dialogTitle ?? ariaLabel}>
        <DialogHeader onClose={() => setDialogOpen(false)}>
          {dialogTitle ? <DialogTitle>{dialogTitle}</DialogTitle> : null}
        </DialogHeader>
        <DialogContent>{content as ReactNode}</DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setDialogOpen(false)}>
            {closeLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
